import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
} from 'react'
import type { Hue } from '../store/types'

/** Inline custom properties that drive `.tint`, `.progress__fill`, `.check` etc. */
export function hueVars(hue?: Hue): CSSProperties {
  if (!hue) return {}
  return {
    ['--tint' as string]: `var(--h-${hue})`,
    ['--tint-wash' as string]: `var(--h-${hue}-wash)`,
  }
}

/* ------------------------------------------------------------------ Button */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'accent' | 'ghost' | 'quiet' | 'danger'
  size?: 'sm' | 'md'
  block?: boolean
  icon?: boolean
}

export function Button({
  variant = 'ghost',
  size = 'md',
  block,
  icon,
  className = '',
  ...rest
}: ButtonProps) {
  const cls = [
    'btn',
    `btn--${variant}`,
    size === 'sm' && 'btn--sm',
    block && 'btn--block',
    icon && 'btn--icon',
    className,
  ]
    .filter(Boolean)
    .join(' ')
  return <button type="button" className={cls} {...rest} />
}

/* -------------------------------------------------------------------- Card */

export function Card({
  children,
  className = '',
  style,
  sunk,
  ...rest
}: {
  children: ReactNode
  className?: string
  style?: CSSProperties
  sunk?: boolean
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`card ${sunk ? 'card--sunk' : ''} ${className}`.trim()}
      style={style}
      {...rest}
    >
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------- Meta */

export function Meta({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`meta ${className}`.trim()}>{children}</div>
}

/* --------------------------------------------------------------- Progress */

export function Progress({ value, hue }: { value: number; hue?: Hue }) {
  const pct = Math.max(0, Math.min(100, value))
  return (
    <div
      className="progress"
      style={hueVars(hue)}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="progress__fill" style={{ width: `${pct}%` }} />
    </div>
  )
}

/* --------------------------------------------------------------- Checkbox */

export function Check({
  checked,
  onChange,
  label,
  hue,
  right,
}: {
  checked: boolean
  onChange: () => void
  label: ReactNode
  hue?: Hue
  right?: ReactNode
}) {
  return (
    <div className="row" style={{ gap: 0 }}>
      <button
        type="button"
        className="check grow"
        role="checkbox"
        aria-checked={checked}
        onClick={onChange}
        style={hueVars(hue)}
      >
        <span className="check__box" aria-hidden="true">
          <CheckGlyph />
        </span>
        <span className="check__label">{label}</span>
      </button>
      {right}
    </div>
  )
}

function CheckGlyph() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2.5 6.2 4.8 8.5 9.5 3.8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* ------------------------------------------------------------------ Sheet */

export function Sheet({
  title,
  onClose,
  children,
  footer,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    // Move focus into the sheet so keyboard and screen-reader users land here.
    ref.current?.querySelector<HTMLElement>('input, textarea, select, button')?.focus()
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  return (
    <div
      className="sheet-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="sheet__head">
          <h2>{title}</h2>
          <Button icon variant="quiet" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </div>
        <div className="sheet__body">{children}</div>
        {footer && <div className="sheet__foot">{footer}</div>}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ Field */

export function Field({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  )
}

/* ------------------------------------------------------------------ Empty */

export function Empty({ title, body }: { title: string; body?: string }) {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      {body && <div className="empty__body">{body}</div>}
    </div>
  )
}

/* ------------------------------------------------------------- ScreenHead */

export function ScreenHead({
  title,
  sub,
  action,
}: {
  title: string
  sub?: string
  action?: ReactNode
}) {
  return (
    <div className="screen-head row" style={{ alignItems: 'flex-start' }}>
      <div className="grow">
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      {action}
    </div>
  )
}
